import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import { getFrameworkDisplayName } from '@/hooks/useComplianceFramework'
import { ControlPill } from '@/components/compliance/shared'
import {
  RoleSelect,
  type RoleSelectRole,
} from '@/components/compliance/RoleSelect'
import { type PolicyItem } from './types'

/** Per-policy review cadence options (mirrors the `EvidenceFrequency` enum the
 *  `updatePolicyMetadata` mutation accepts). Changing the cadence re-computes
 *  `nextReviewDate` server-side from the last successful review. */
const CADENCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SEMI_ANNUAL', label: 'Semi-annual' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ONCE', label: 'One-time' },
]
const CADENCE_LABEL: Record<string, string> = Object.fromEntries(
  CADENCE_OPTIONS.map((o) => [o.value, o.label]),
)

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='space-y-1'>
      <div className='text-[10px] font-semibold tracking-wider text-text-placeholder uppercase'>
        {label}
      </div>
      {children}
    </div>
  )
}

/**
 * Vertical "Properties" panel for a policy — owner / approver, per-policy
 * review cadence + next-review date, and mapped controls. Lives in the policy
 * detail page's right sidebar (Properties tab). Reuses the same
 * `updatePolicyMetadata` mutation. Read-only when the policy is covered by a
 * master or the viewer can't edit.
 *
 * Per-policy review cadence (re-added): the review unit is the PolicyDocument,
 * which spans system, imported, and custom policies regardless of slug. Each
 * policy carries its own cadence + next-review date; the consolidated "Periodic
 * Policy Review" test rolls these up. (This supersedes RFC-195 §3.1's single
 * workspace-wide cadence, which couldn't represent custom/no-slug policies.)
 */
export function PolicyPropertiesPanel({
  policyData,
  programId,
  readOnly = false,
  mappedControls = [],
}: {
  policyData?: PolicyItem
  programId: string
  readOnly?: boolean
  /** Framework controls this policy maps to, with each control's human title
   * (e.g. §164.312(b) → "Audit Controls"). `title` is null where the catalog
   * has no citation title (e.g. SOC 2 criteria) and the ref is shown instead. */
  mappedControls?: { framework: string; ref: string; title: string | null }[]
}) {
  const queryClient = useQueryClient()
  const rolesQuery = useQuery(trpc.complianceRole.list.queryOptions())
  const roles = (rolesQuery.data ?? []) as RoleSelectRole[]

  const metaMutation = useMutation(
    trpc.complianceProgram.updatePolicyMetadata.mutationOptions()
  )
  const { mutate: updatePolicyMetadata } = metaMutation

  const updateField = useCallback(
    (field: string, value: string | null) => {
      if (!policyData?.slug) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(updatePolicyMetadata as any)(
        { programId, policySlug: policyData.slug, [field]: value },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: trpc.complianceProgram.getPolicies.queryKey(),
            })
            // Keep the Periodic Policy Review roll-up panel in sync.
            queryClient.invalidateQueries({
              queryKey:
                trpc.complianceProgram.getPolicyReviewStatus.queryKey(),
            })
          },
        }
      )
    },
    [programId, policyData?.slug, updatePolicyMetadata, queryClient]
  )

  const readOnlyValue = (text: string) => (
    <div className='text-[12px] font-medium text-text-body'>{text}</div>
  )

  return (
    <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
      <div className='space-y-4 p-4'>
        <Field label='Owner'>
          <RoleSelect
            value={policyData?.ownerRoleId ?? null}
            roles={roles}
            readOnly={readOnly}
            onValueChange={(v) => updateField('ownerRoleId', v)}
            ariaLabel='Policy owner role'
            className='w-full justify-start gap-0 [&>svg]:ml-auto'
          />
        </Field>

        <Field label='Approver'>
          <RoleSelect
            value={policyData?.approverRoleId ?? null}
            roles={roles}
            readOnly={readOnly}
            onValueChange={(v) => updateField('approverRoleId', v)}
            ariaLabel='Policy approver role'
            className='w-full justify-start gap-0 [&>svg]:ml-auto'
          />
          {!readOnly &&
            policyData?.approverRoleId &&
            policyData?.ownerRoleId === policyData.approverRoleId && (
              <div className='text-[11px] text-status-orange'>
                For separation of duties, pick a different approver where
                possible.
              </div>
            )}
        </Field>

        <Field label='Review cadence'>
          {readOnly ? (
            readOnlyValue(
              CADENCE_LABEL[policyData?.reviewFrequency ?? ''] ??
                policyData?.reviewFrequency ??
                'Annual',
            )
          ) : (
            <Select
              value={policyData?.reviewFrequency ?? 'ANNUAL'}
              onValueChange={(v) => updateField('reviewFrequency', v)}
            >
              <SelectTrigger className='w-full justify-start gap-0 [&>svg]:ml-auto'>
                <SelectValue placeholder='Annual' />
              </SelectTrigger>
              <SelectContent>
                {CADENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label='Next review'>
          {readOnly ? (
            readOnlyValue(
              policyData?.nextReviewDate
                ? new Date(policyData.nextReviewDate).toLocaleDateString(
                    undefined,
                    { year: 'numeric', month: 'short', day: 'numeric' },
                  )
                : '—',
            )
          ) : (
            <DatePicker
              value={
                policyData?.nextReviewDate
                  ? new Date(policyData.nextReviewDate)
                  : undefined
              }
              onChange={(d) =>
                updateField('nextReviewDate', d ? d.toISOString() : null)
              }
              placeholder='Set from cadence'
            />
          )}
          {!readOnly && (
            <div className='text-[11px] text-text-placeholder'>
              Auto-set from the cadence after each approval; override here if you
              reviewed on a different date.
            </div>
          )}
        </Field>

        {mappedControls.length > 0 && (
          <Field label='Mapped controls'>
            <div className='space-y-1.5'>
              {mappedControls.map((mc) => (
                <div
                  key={`${mc.framework}:${mc.ref}`}
                  className='flex items-center justify-between gap-3'
                >
                  <span className='text-[12px] font-medium text-text-secondary'>
                    {mc.title ?? mc.ref}
                  </span>
                  <ControlPill
                    frameworkName={getFrameworkDisplayName(mc.framework)}
                    controlId={mc.ref}
                    title={mc.title ?? undefined}
                    className='shrink-0'
                  />
                </div>
              ))}
            </div>
          </Field>
        )}
      </div>
    </ScrollArea>
  )
}
